import { useEffect, useState } from "react";
import { searchKeywords, type KeywordMatch } from "@/lib/tobis";

// Entprellte Keyword-Suche gegen den TMDB-Katalog, geteilt von der
// Titel-Filterleiste (TobisKeywordFilter) und dem Pitch-Schritt im Labor
// (TobisLabSessionPage).
//
// Als Hook und nicht als Komponente: die beiden Stellen sehen voellig
// verschieden aus - dort ein schmales Dropdown in einer Filterzeile, hier ein
// breites Formularfeld mit Vorschlagsliste. Gemeinsam ist nur das Verhalten:
// tippen, warten, fragen, Ergebnis mit Trefferzahl anzeigen. Genau das steht
// hier, das Aussehen bleibt bei den Aufrufern.
//
// Bei rund 19.700 Keywords im Bestand geht kein Weg an der Serveranfrage
// vorbei; eine vorab geladene Liste waere weder ladbar noch durchsuchbar.

const DEBOUNCE_MS = 300;

// Die volle Liste eines Begriffs statt der 50 aus dem Default: "drama" hat 68
// Treffer ab drei Titeln, "man" 86 - bei den haeufigen Woertern fehlte bisher
// rund ein Viertel, ohne jeden Hinweis. 300 deckt jeden gemessenen Begriff ab
// und bleibt eine Obergrenze, damit ein einzelner Buchstabe nicht den ganzen
// Bestand zieht (32.983 Keywords, davon 13.618 ab drei Titeln).
const MAX_SUGGESTIONS = 300;

export interface KeywordSuggestions {
  /** Treffer zur aktuellen Eingabe. Leer, solange nichts eingegeben ist. */
  suggestions: KeywordMatch[];
  loading: boolean;
  /** Eingabe vorhanden, Suche fertig, kein Treffer - traegt den Leer-Hinweis. */
  empty: boolean;
}

export function useKeywordSuggestions(query: string): KeywordSuggestions {
  const [suggestions, setSuggestions] = useState<KeywordMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const term = query.trim();

  useEffect(() => {
    if (!term) {
      // Zuruecksetzen und nicht nur ausblenden: sonst blitzt beim naechsten
      // Tippen der Treffer des vorigen Begriffs auf, bevor die Antwort da ist.
      setSuggestions([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const timer = window.setTimeout(() => {
      void searchKeywords([term], MAX_SUGGESTIONS)
        .then(setSuggestions)
        .catch(() => setSuggestions([]))
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [term]);

  return { suggestions, loading, empty: term !== "" && !loading && suggestions.length === 0 };
}
